import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

// Inject an updated <title>, meta description, canonical + JSON-LD Article
// for each individual post — critical for SEO.
function useArticleMeta(post) {
  useEffect(() => {
    if (!post) return;
    const originalTitle = document.title;

    document.title = post.seo_title || `${post.title} | Rubiemaze Journal`;

    const ensureMeta = (attrName, attrVal, content) => {
      let el = document.querySelector(`meta[${attrName}="${attrVal}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };
    const desc = ensureMeta(
      "name",
      "description",
      post.seo_description || post.excerpt,
    );
    const ogTitle = ensureMeta("property", "og:title", post.title);
    const ogDesc = ensureMeta(
      "property",
      "og:description",
      post.seo_description || post.excerpt,
    );
    const ogImg = ensureMeta("property", "og:image", post.cover_image || "");
    const ogUrl = ensureMeta(
      "property",
      "og:url",
      `https://rubiemaze.com/blog/${post.slug}`,
    );

    // Article JSON-LD
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.seo_description || post.excerpt,
      image: post.cover_image,
      author: {
        "@type": "Person",
        name: "Ritesh Sikri",
        url: "https://rubiemaze.com/",
      },
      publisher: {
        "@type": "Organization",
        name: "Rubiemaze Inc",
      },
      datePublished: post.published_at,
      mainEntityOfPage: `https://rubiemaze.com/blog/${post.slug}`,
      keywords: (post.tags || []).join(", "),
    });
    ld.setAttribute("data-article-jsonld", "true");
    document.head.appendChild(ld);

    return () => {
      document.title = originalTitle;
      ld.remove();
    };
  }, [post]);
}

const mdComponents = {
  h2: (props) => (
    <h2
      className="font-display text-white text-2xl md:text-4xl mt-14 mb-4 leading-tight tracking-[-0.01em]"
      {...props}
    />
  ),
  h3: (props) => (
    <h3
      className="font-display text-white text-xl md:text-2xl mt-10 mb-3 leading-tight"
      {...props}
    />
  ),
  p: (props) => (
    <p
      className="text-white/75 text-[16px] md:text-[17px] leading-[1.75] my-5"
      {...props}
    />
  ),
  ul: (props) => (
    <ul
      className="list-none pl-0 space-y-2 my-6 text-white/75"
      {...props}
    />
  ),
  ol: (props) => (
    <ol
      className="list-decimal pl-6 space-y-2 my-6 text-white/75 marker:text-[#D4AF37]"
      {...props}
    />
  ),
  li: ({ children, ...rest }) => (
    <li
      className="pl-6 relative before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:rounded-full before:bg-[#D4AF37]/70 text-[16px] md:text-[17px] leading-[1.7]"
      {...rest}
    >
      {children}
    </li>
  ),
  strong: (props) => (
    <strong className="text-white font-medium" {...props} />
  ),
  em: (props) => (
    <em className="font-editorial italic text-[#D4AF37]" {...props} />
  ),
  blockquote: (props) => (
    <blockquote
      className="my-8 pl-6 border-l-2 border-[#D4AF37] font-editorial italic text-white/85 text-xl md:text-2xl leading-snug"
      {...props}
    />
  ),
  a: (props) => (
    <a
      className="text-[#D4AF37] underline underline-offset-4 hover:text-[#f3d568]"
      target="_blank"
      rel="noreferrer"
      {...props}
    />
  ),
  hr: () => <hr className="my-12 border-white/10" />,
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const res = await axios.get(`${API}/posts/${slug}`);
        if (!cancelled) setPost(res.data);
      } catch (err) {
        if (!cancelled) setError("Post not found.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useArticleMeta(post);

  if (loading) {
    return (
      <section className="min-h-screen bg-[#030303] pt-40 pb-20 text-center text-white/50 text-sm">
        Loading essay…
      </section>
    );
  }

  if (error || !post) {
    return (
      <section className="min-h-screen bg-[#030303] pt-40 pb-20 text-center">
        <p className="text-white/60 mb-6">Essay not found.</p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#f3d568]"
        >
          Back to Journal
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12h14M13 5l7 7-7 7"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        </Link>
      </section>
    );
  }

  return (
    <article
      data-testid="blog-post"
      className="relative bg-[#030303] pt-28 md:pt-36 pb-20 md:pb-32"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Link
            to="/blog"
            data-testid="blog-back"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-white/50 hover:text-[#D4AF37] transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M19 12H5M11 19l-7-7 7-7"
                stroke="currentColor"
                strokeWidth="1.8"
              />
            </svg>
            The Journal
          </Link>

          <div className="mt-8 flex items-center gap-3 text-[11px] uppercase tracking-[0.24em] text-white/45">
            <span>{formatDate(post.published_at)}</span>
            <span className="text-white/25">·</span>
            <span>{post.reading_time} min read</span>
            <span className="text-white/25">·</span>
            <span className="text-[#D4AF37]">By Ritesh Sikri</span>
          </div>

          <h1
            data-testid="blog-post-title"
            className="mt-6 font-display text-white text-[32px] sm:text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-[-0.02em]"
          >
            {post.title}
          </h1>

          <p className="mt-6 text-white/70 text-lg md:text-xl leading-relaxed font-editorial italic">
            {post.excerpt}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.9 }}
          className="mt-12 relative aspect-[16/10] w-full overflow-hidden rounded-2xl border border-white/10"
        >
          <img
            src={post.cover_image}
            alt={post.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        </motion.div>

        <div className="mt-12 md:mt-16 prose prose-invert max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
            {post.content}
          </ReactMarkdown>
        </div>

        {/* CTA at the end */}
        <div className="mt-16 md:mt-24 p-8 md:p-10 rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-b from-[#141005] to-[#0a0a0a]">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3">
            Ready to work together?
          </div>
          <h3 className="font-display text-white text-2xl md:text-3xl leading-tight">
            Book a paid strategy call — or a full nightclub audit.
          </h3>
          <p className="mt-4 text-white/70 text-sm md:text-base max-w-xl">
            If any of the above resonated with your venue, the next step is
            usually a 90-minute paid call. Let's put a number on the leak.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/#contact"
              data-testid="blog-cta-book"
              className="inline-flex items-center justify-center gap-3 rounded-full bg-[#D4AF37] text-black px-6 py-3.5 text-sm font-medium hover:bg-[#f3d568] transition-colors"
            >
              Book a Paid Call
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12h14M13 5l7 7-7 7"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
              </svg>
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center justify-center gap-3 rounded-full border border-white/20 text-white px-6 py-3.5 text-sm font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              Read another essay
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}
